import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  MdGridView,
  MdShowChart,
  MdPieChart,
  MdTrackChanges,
  MdBolt,
  MdArticle,
  MdSlideshow,
  MdCandlestickChart,
  MdInsights,
  MdMenu,
} from 'react-icons/md';
import { useAuth } from '../../context/AuthContext';

const sections = [
  {
    label: 'WORKSPACE',
    items: [
      { key: 'overview', label: 'Overview', icon: MdGridView, path: '/' },
      { key: 'hf', label: 'HF Analytics', icon: MdShowChart, path: '/hf' },
      { key: 'saa', label: 'Asset Allocation', icon: MdPieChart, path: '/saa' },
    ],
  },
  {
    label: 'INTELLIGENCE',
    items: [
      { key: 'saudi', label: 'Saudi Market', icon: MdCandlestickChart, path: '/saudi' },
      { key: 'gcc', label: 'GCC Flows', icon: MdShowChart, path: '/gcc' },
      { key: 'signals', label: 'Technical Signals', icon: MdInsights, path: '/signals' },
      { key: 'market', label: 'Market Tracker', icon: MdTrackChanges, path: '/market', disabled: true },
      { key: 'trading', label: 'Auto Trading', icon: MdBolt, path: '/trading' },
      { key: 'researchos', label: 'ResearchOS', icon: MdInsights, path: '/researchos' },
    ],
  },
  {
    label: 'REPORTS',
    items: [
      { key: 'briefs', label: 'Daily Briefs', icon: MdArticle, path: '/researchos/briefs', superuserOnly: true },   // Courier (ResearchOS; backend gates too)
      { key: 'decks', label: 'IC Decks', icon: MdSlideshow, path: '/decks', disabled: true },
    ],
  },
];

const Sidebar = ({ isOpen, onNavClick, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();

  const perms = user?.profile?.app_permissions || {};

  // Delegated OPERATOR (e.g. Joji on the Alternatives Portfolio): reaches the trading module
  // through an AccountDelegate, NOT through access_trading — so the normal perms.trading gate
  // would hide it. Show it (relabelled "Trading Engine") for a delegate who is not an admin.
  const isOperator = !!(user?.trading_delegate?.is_delegate) && !user?.is_admin;

  // Map sidebar keys to permission keys
  const permMap = { hf: 'hf', saa: 'saa', saudi: 'saudi', gcc: 'saudi', signals: 'signals', market: 'market', trading: 'trading' };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const initials = user?.username ? user.username.charAt(0).toUpperCase() : 'U';

  return (
    <div
      className={`
        fixed inset-y-0 left-0 z-50
        bg-[#f7f9fc] dark:bg-[#0d1117]
        border-r border-gray-200 dark:border-gray-800
        flex flex-col
        transition-all duration-300 ease-in-out
        ${isOpen ? 'w-[200px] translate-x-0' : 'w-[56px] -translate-x-full lg:translate-x-0'}
      `}
    >
      {/* Brand */}
      <div className="px-4 pt-5 pb-4 flex items-center justify-center">
        {isOpen ? (
          <div className="cursor-pointer" onClick={onToggle}>
            <div className="font-serif text-lg text-gray-900 dark:text-gray-100">
              Maghaweer<span className="text-[#c9a96e]">.ai</span>
            </div>
            <div className="text-[8px] uppercase tracking-[2px] text-gray-400 dark:text-gray-600 mt-0.5">
              CAPITAL MARKETS PLATFORM
            </div>
          </div>
        ) : (
          <div
            className="text-[#c9a96e] cursor-pointer select-none"
            onClick={onToggle}
          >
            <MdMenu size={22} />
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-2">
        {sections.map((section) => (
          <div key={section.label} className="mb-3">
            {isOpen && (
              <div className="text-[9px] uppercase tracking-[2px] text-gray-400 dark:text-gray-600 px-2 mb-1.5">
                {section.label}
              </div>
            )}
            {section.items.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);

              // A delegate operator sees the trading module (as "Trading Engine") even without
              // access_trading; every other module still follows the normal perms gate.
              const operatorTrading = item.key === 'trading' && isOperator;
              const label = operatorTrading ? 'Trading Engine' : item.label;

              // Hide items user doesn't have permission for
              const permKey = permMap[item.key];
              if (permKey && perms[permKey] === false && !operatorTrading) return null;
              if (item.superuserOnly && !user?.is_admin) return null;   // superuser-only surfaces (Courier)

              if (item.disabled) {
                return (
                  <div
                    key={item.key}
                    className={`flex items-center gap-2.5 py-2 text-gray-400 dark:text-gray-600 cursor-not-allowed rounded-md ${isOpen ? 'px-2 text-sm' : 'px-0 justify-center'}`}
                    title={!isOpen ? item.label : undefined}
                  >
                    <Icon className="text-lg shrink-0" />
                    {isOpen && (
                      <>
                        <span className="flex-1">{item.label}</span>
                        <span className="text-[8px] bg-gray-100 dark:bg-gray-800 text-gray-400 px-1.5 py-0.5 rounded">Soon</span>
                      </>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.key}
                  to={item.path}
                  end={item.path === '/'}
                  className={`
                    flex items-center gap-2.5 py-2 rounded-md transition-colors
                    ${isOpen ? 'px-2 text-sm' : 'px-0 justify-center'}
                    ${active
                      ? 'text-[#c9a96e] bg-[#c9a96e]/5 border-l-2 border-[#c9a96e]'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'}
                  `}
                  onClick={onNavClick}
                  title={!isOpen ? item.label : undefined}
                >
                  <Icon className="text-lg shrink-0" />
                  {isOpen && <span>{item.label}</span>}
                </NavLink>
              );
            })}
          </div>
        ))}
      </nav>

      {/* User Footer */}
      <div className="px-2 py-3 border-t border-gray-200 dark:border-gray-800">
        <div className={`flex items-center ${isOpen ? 'gap-2.5 px-2' : 'justify-center'}`}>
          <div className="w-8 h-8 rounded-full bg-[#c9a96e]/10 text-[#c9a96e] flex items-center justify-center text-xs font-semibold shrink-0">
            {initials}
          </div>
          {isOpen && (
            <div className="min-w-0">
              <div className="text-sm text-gray-900 dark:text-gray-100 font-medium truncate">{user?.username || 'User'}</div>
              <div className="text-[10px] text-gray-400 dark:text-gray-600">Principal</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
